import type { ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  action?: ReactNode;
  className?: string;
  /** Use "inline" for inside a table/card, "page" for full page */
  variant?: 'inline' | 'page';
}

export function ErrorState({
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
  onRetry,
  action,
  className,
  variant = 'inline',
}: ErrorStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 text-center',
        variant === 'page'
          ? 'min-h-[60vh]'
          : 'rounded-xl border border-destructive/20 bg-destructive/5 py-14',
        className,
      )}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 ring-4 ring-destructive/10">
        <AlertTriangle className="h-7 w-7 text-destructive" />
      </div>

      <div className="space-y-1 max-w-xs">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {message && (
          <p className="text-xs leading-relaxed text-muted-foreground">{message}</p>
        )}
      </div>

      {(onRetry || action) && (
        <div className="flex items-center gap-2 mt-1">
          {onRetry && (
            <Button size="sm" variant="outline" onClick={onRetry} className="gap-2">
              <RefreshCw className="h-3.5 w-3.5" />
              Try again
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}
